// Factory function pattern to avoid Webpack TDZ issues with class inheritance
// This uses BaseNotaryFactory instead of class inheritance

import { fetchWithNetworkLogging } from "@Front/Api/helpers/requestHelpers";
import { createBaseNotary, type IBaseNotary } from "../BaseNotaryFactory";
import { LoggerService } from "@Front/Services/LoggerService/LoggerService";
import { userFacingMessageFromApiClientError } from "@Front/Utils/userFacingMessageFromApiClientError";
import { ContentType } from "@Front/Api/ContentType";
import type { ISearchNotariesResponse } from "./SearchNotaries";

interface SearchNotariesInstance {
	searchNotaries(query: string, folderUid: string): Promise<ISearchNotariesResponse>;
}

// Module-level singleton instance
let instance: SearchNotariesInstance | null = null;
let baseNotaryInstance: IBaseNotary | null = null;

function createSearchNotaries(): SearchNotariesInstance {
	if (!baseNotaryInstance) {
		baseNotaryInstance = createBaseNotary();
	}

	const baseNotary = baseNotaryInstance;

	return {
		searchNotaries: (query: string, folderUid: string) => doSearchNotaries(query, folderUid, baseNotary),
	};
}

function normalizeSearchNotariesError(err: unknown, query: string): ISearchNotariesResponse {
	LoggerService.error("❌ Erreur recherche notaires", { query, error: err });
	if (err && typeof err === "object" && "results" in err && "annuaireConfigured" in err) {
		return err as ISearchNotariesResponse;
	}
	return {
		results: [],
		annuaireConfigured: true,
		message: userFacingMessageFromApiClientError(err, "Impossible de récupérer les notaires. Réessayez plus tard."),
	};
}

async function doSearchNotaries(
	query: string,
	folderUid: string,
	baseNotary: IBaseNotary,
): Promise<ISearchNotariesResponse> {
	if (!query || query.length < 2 || !folderUid?.trim()) {
		return { results: [], annuaireConfigured: true };
	}
	const url = new URL(baseNotary.namespaceUrl.concat(`/search/notaries`));
	url.searchParams.set("q", query);
	url.searchParams.set("folderUid", folderUid.trim());
	try {
		const headers = baseNotary.buildHeaders(ContentType.JSON);
		const response = await fetchWithNetworkLogging(url, { method: "GET", headers, credentials: "include" });
		if (response.status === 503) {
			return (await response.json()) as ISearchNotariesResponse;
		}
		if (!response.ok) {
			const text = await response.text();
			throw new Error(text || `HTTP ${response.status}`);
		}
		return (await response.json()) as ISearchNotariesResponse;
	} catch (err) {
		return normalizeSearchNotariesError(err, query);
	}
}

export function getSearchNotariesInstance(): SearchNotariesInstance {
	if (!instance) {
		instance = createSearchNotaries();
	}
	return instance;
}
